<template>
  <div
    tabindex="1"
    class="select-container"
    @focus="showOptions"
    @blur="handleBlur"
  >
    <div
      ref="mainContainer"
      :class="[disabled ? 'bg-accent-disabled-multiselect' : 'bg-accent-multiselect']"
    >
      <div class="w-11/12 flex flex-row items-center">
        <span
          v-if="!selected.length"
          class="w-11/12"
          :class="[selected.length && !disabled ? 'text-grey' : 'text-grey-001']"
        >{{ placeholder }}</span>
        <div
          v-else
          class="flex flex-row items-center"
        >
          <div
            v-for="(item, index) of selected"
            :key="index"
            :class="[disabled ? 'bg-disabled-tag' : 'bg-tag']"
          >
            <span :class="[disabled ? 'text-grey' : 'text-primary']">{{ item.text }}</span>
            <vs-icon
              icon="close"
              size="10px"
              class="ml-1"
              :color="disabled ? '#3D3D3D': '#493493'"
              @click="removeItem(item)"
            />
          </div>
        </div>
      </div>
      <div class="w-1/12 flex flex-row items-center justify-end">
        <vs-icon
          :icon="isOptionShown ? 'arrow_drop_up' : 'arrow_drop_down'"
          :color="disabled ? '#AAAAAA': '#493493'"
        />
      </div>
    </div>
    <div
      v-show="isOptionShown"
      class="bg-option-container"
    >
      <div class="bg-inner-option-container">
        <div
          v-if="!options.length"
          class="flex flex-row items-center justify-center"
        >
          <span class="empty-option">Nothing can be shown</span>
        </div>
        <ul v-else>
          <li
            v-for="(item, index) of options"
            class="my-1"
            :key="index"
          >
            <div class="flex flex-row gap-2">
              <div class="w-11/12 flex flex-row items-center">
                <span>{{ item.text }}</span>
              </div>
              <div class="w-1/12 flex flex-row items-center justify-end">
                <transition name="spin">
                  <div
                    class="p-1 flex flex-row border border-secondary rounded-xl"
                    :key="checkMarkItem(item)"
                    :class="[checkMarkItem(item) ? 'bg-secondary' : 'bg-white']"
                    @click="selectItem(item)"
                  >
                    <vs-icon
                      icon="check"
                      color="#FFFFFF"
                      size="8px"
                    />
                  </div>
                </transition>
              </div>
            </div>
          </li>
        </ul>
      </div>
      <div class="bg-inner-footer-container">
        <vs-button
          type="filled"
          color="secondary"
          class="w-32 rounded-lg font-semibold"
          :disabled="!options.length"
          @click="confirmItem"
        >
          Select
        </vs-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    /**
     * `value` or `v-model` will returned
     * `[{ value: 'something', text: 'something' }]`
     */
    value: {
      type: null,
      default: () => null
    },
    placeholder: {
      type: String,
      default: () => 'Select option'
    },
    disabled: {
      type: Boolean,
      default: () => false
    },
    isValid: {
      type: Boolean,
      default: () => true
    },
    /**
     * Array of available options: Objects.
     * Objects must consist of `value` and `text`
     * example: `{ value: 'something', text: 'something' }`
     */
    options: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isOptionShown: false,
      selected: this.value,
      tempSelected: []
    }
  },
  watch: {
    value: {
      handler(val) {
        this.selected = val
      }
    },
    isValid: {
      handler(val) {
        if (!this.disabled) {
          if (val) {
            this.$refs.mainContainer.classList.remove('bg-accent-danger-multiselect')
            this.$refs.mainContainer.classList.add('bg-accent-multiselect')
            return
          }

          this.$refs.mainContainer.classList.remove('bg-accent-multiselect')
          this.$refs.mainContainer.classList.add('bg-accent-danger-multiselect')
          return
        }
        this.$refs.mainContainer.classList.add('bg-accent-disabled-multiselect')
      }
    }
  },
  methods: {
    showOptions() {
      if (!this.disabled && !this.isOptionShown) {
        /**
         * if `selected` is not mapped,
         * `selected` will be following `tempSelected`
         * when `tempSelected` react to data change
         */
        this.tempSelected = this.selected.map(item => item)
        this.isOptionShown = true

        this.$el.focus()
      }
    },
    closeOptions() {
      if (!this.disabled && this.isOptionShown) {
        this.isOptionShown = false

        this.$el.blur()
      }
    },
    handleBlur(ev) {
      if (!ev.currentTarget.contains(ev.relatedTarget)) {
        this.closeOptions()
      }
    },
    selectItem(item) {
      const isItemHasSelected = this.tempSelected.find(selectedItem => item.value === selectedItem.value)
      if (isItemHasSelected) {
        this.tempSelected = this.tempSelected.filter(selectedItem => selectedItem.value !== item.value)
        return
      }

      this.tempSelected = []
      this.tempSelected.push(item)
    },
    checkMarkItem(option) {
      const existedItem = this.tempSelected.find(selectedItem => selectedItem.value === option.value)

      if (existedItem) {
        return this.options.some(item => item.value === existedItem.value)
      }
      return false
    },
    removeItem(item) {
      if (this.disabled) {
        return
      }

      this.isOptionShown = false
      this.$el.blur()

      this.selected = this.selected.filter(selectedItem => selectedItem.value !== item.value)
      this.$emit('input', this.selected)
    },
    confirmItem() {
      this.selected = this.tempSelected
      this.$emit('input', this.selected)

      this.closeOptions()
    }
  }
}
</script>

<style lang="scss" scoped>
li {
  @apply p-3 cursor-pointer;

  color: #3D3D3D;
}

.select-container {
  @apply w-full relative;
}

.bg-accent-multiselect {
  @apply p-3 w-full flex flex-row border-transparent rounded-lg cursor-pointer;

  height: calc(1rem * 10 / 3);
  background-color: #F1F1FD;
}

.bg-accent-disabled-multiselect {
  @apply p-3 w-full flex flex-row border-transparent rounded-lg cursor-not-allowed;

  height: calc(1rem * 10 / 3);
  background-color: #ECEBED;
}

.bg-accent-danger-multiselect {
  @apply p-3 w-full flex flex-row border-2 border-danger rounded-lg cursor-pointer;

  height: calc(1rem * 10 / 3);
  background-color: #F1F1FD;
}

.bg-option-container {
  @apply mt-1 w-full flex flex-col absolute bg-white rounded-lg shadow-lg z-10;

  height: 20rem;
}

.bg-inner-option-container {
  @apply p-3 mt-3 flex flex-col overflow-auto;

  height: 15rem;
}

.bg-inner-footer-container {
  @apply p-3 flex flex-row items-center justify-end shadow-inner;
}

.bg-tag, .bg-disabled-tag {
  @apply px-2 py-1 mr-2 flex flex-row items-center text-xs rounded;

  background-color: #CDC6E1;
}

.bg-disabled-tag {
  @apply bg-grey-001;
}

.empty-option {
  @apply italic text-center;

  color: #3D3D3D;
}

.spin-enter-active {
  animation: spin 0.5s;
}

.spin-leave-active {
  opacity: 0;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  50% {
    transform: rotate(90deg);
  }

  100% {
    transform: rotate(0deg);
  }
}
</style>
